import * as React from 'react'

import { cn } from '@/lib/utils'
import { clampZoomValue } from './constants'
import {
  BRACKET_STAGE_PADDING,
  getBracketContentPlacementStyle,
  getBracketStageLayout,
  getInitialBracketZoom,
} from './stage'
import { getBracketVisibleArea, type BracketVisibleArea } from './viewport'
import type { BracketBackgroundPosition, PanOffset } from './types'

type BracketViewportProps = {
  backgroundLayerStyle: React.CSSProperties | null
  backgroundOverlayStyle: React.CSSProperties
  bracketHeight: number
  bracketWidth: number
  children: (area: BracketVisibleArea | null) => React.ReactNode
  className: string
  isFullscreen: boolean
  isPrinting: boolean
  label: string
  onZoomChange: (zoom: number) => void
  position: BracketBackgroundPosition
  printBackground: boolean
  renderAllMatches: boolean
  stageRef: React.RefObject<HTMLDivElement | null>
  zoom: number
}

export function BracketViewport(props: BracketViewportProps) {
  const {
    backgroundLayerStyle,
    backgroundOverlayStyle,
    bracketHeight,
    bracketWidth,
    children,
    className,
    isFullscreen,
    isPrinting,
    label,
    position,
    printBackground,
    renderAllMatches,
    stageRef,
    zoom,
  } = props
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const sizeRef = React.useRef({ width: 0, height: 0 })
  const rectRef = React.useRef<DOMRect | null>(null)
  const panRef = React.useRef<PanOffset>({ x: 0, y: 0 })
  const dragRef = React.useRef<{ start: PanOffset; origin: PanOffset } | null>(
    null,
  )
  const userPannedRef = React.useRef(false)
  const initiallyFittedRef = React.useRef(false)
  const frameRef = React.useRef<number | null>(null)
  const settingsRef = React.useRef(props)
  settingsRef.current = props
  const [area, setArea] = React.useState<BracketVisibleArea | null>(null)

  const schedulePaint = React.useCallback(() => {
    if (frameRef.current !== null) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      const settings = settingsRef.current
      const pan = panRef.current
      if (settings.stageRef.current) {
        settings.stageRef.current.style.transform = settings.isPrinting
          ? 'none'
          : `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${settings.zoom})`
      }
      if (!sizeRef.current.width || !sizeRef.current.height) return
      const next = getBracketVisibleArea({
        bracketWidth: settings.bracketWidth,
        bracketHeight: settings.bracketHeight,
        viewportWidth: sizeRef.current.width,
        viewportHeight: sizeRef.current.height,
        zoom: settings.zoom,
        pan,
        position: settings.position,
      })
      setArea((current) =>
        current &&
        current.left === next.left &&
        current.top === next.top &&
        current.width === next.width &&
        current.height === next.height
          ? current
          : next,
      )
    })
  }, [])

  const center = React.useCallback(() => {
    const settings = settingsRef.current
    if (!userPannedRef.current && !dragRef.current) {
      panRef.current = getBracketStageLayout({
        bracketWidth: settings.bracketWidth,
        bracketHeight: settings.bracketHeight,
        viewportWidth: sizeRef.current.width,
        viewportHeight: sizeRef.current.height,
        zoom: settings.zoom,
      }).pan
    }
    schedulePaint()
  }, [schedulePaint])

  React.useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    // 异步获取视口尺寸，避免大图提交后同步读取布局并再次渲染整个编辑器。
    const observer = new ResizeObserver(([entry]) => {
      sizeRef.current = {
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      }
      rectRef.current = null
      const settings = settingsRef.current
      if (
        !settings.isPrinting &&
        !initiallyFittedRef.current &&
        sizeRef.current.width > 0 &&
        sizeRef.current.height > 0
      ) {
        // 所有页面首次打开采用同一规则，后续窗口变化尊重用户的缩放。
        initiallyFittedRef.current = true
        settings.onZoomChange(
          getInitialBracketZoom({
            bracketWidth: settings.bracketWidth,
            bracketHeight: settings.bracketHeight,
            viewportWidth: sizeRef.current.width,
            viewportHeight: sizeRef.current.height,
          }),
        )
      }
      center()
    })
    observer.observe(viewport)
    const invalidateRect = () => {
      rectRef.current = null
    }
    window.addEventListener('scroll', invalidateRect, {
      capture: true,
      passive: true,
    })
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', invalidateRect, true)
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [center])

  React.useLayoutEffect(() => {
    userPannedRef.current = false
    center()
  }, [bracketHeight, bracketWidth, position, center])
  React.useLayoutEffect(() => {
    center()
  }, [zoom, isPrinting, center])

  const startPan = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (
      event.button !== 0 ||
      target.closest('input, textarea, button, [role="button"], a')
    )
      return
    const active = document.activeElement
    if (
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement
    )
      active.blur()
    dragRef.current = {
      start: { x: event.clientX, y: event.clientY },
      origin: { ...panRef.current },
    }
    event.currentTarget.style.cursor = 'grabbing'
    event.currentTarget.style.userSelect = 'none'
    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
  }
  const movePan = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = event.clientX - drag.start.x
    const dy = event.clientY - drag.start.y
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) userPannedRef.current = true
    panRef.current = { x: drag.origin.x + dx, y: drag.origin.y + dy }
    // 每帧只写一次 transform，连续 pointermove 不触发整个 BracketMaker 渲染。
    schedulePaint()
  }
  const endPan = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    dragRef.current = null
    event.currentTarget.style.cursor = 'grab'
    event.currentTarget.style.userSelect = 'auto'
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId)
    schedulePaint()
  }
  const handleWheel = React.useCallback(
    (event: WheelEvent) => {
      if (
        !settingsRef.current.isFullscreen ||
        !viewportRef.current ||
        event.deltaY === 0
      )
        return
      if (event.cancelable) event.preventDefault()
      const currentZoom = settingsRef.current.zoom
      const nextZoom = clampZoomValue(currentZoom - event.deltaY / 500)
      if (nextZoom === currentZoom) return
      userPannedRef.current = true
      // 视口位置只在滚动或尺寸改变后重读，缩放过程中复用。
      const rect =
        rectRef.current ?? viewportRef.current.getBoundingClientRect()
      rectRef.current = rect
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      const ratio = nextZoom / currentZoom
      panRef.current = {
        x: x - (x - panRef.current.x) * ratio,
        y: y - (y - panRef.current.y) * ratio,
      }
      settingsRef.current = { ...settingsRef.current, zoom: nextZoom }
      settingsRef.current.onZoomChange(nextZoom)
      schedulePaint()
    },
    [schedulePaint],
  )

  React.useEffect(() => {
    const viewport = viewportRef.current
    if (!isFullscreen || !viewport) return
    // 全屏时显式使用非被动监听，避免缩放同时触发页面滚动。
    viewport.addEventListener('wheel', handleWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', handleWheel)
  }, [isFullscreen, handleWheel])
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    const offsets: Record<string, PanOffset> = {
      ArrowLeft: { x: 80, y: 0 },
      ArrowRight: { x: -80, y: 0 },
      ArrowUp: { x: 0, y: 80 },
      ArrowDown: { x: 0, y: -80 },
    }
    const offset = offsets[event.key]
    if (!offset) return
    event.preventDefault()
    userPannedRef.current = true
    panRef.current = {
      x: panRef.current.x + offset.x,
      y: panRef.current.y + offset.y,
    }
    schedulePaint()
  }

  return (
    <div
      data-bracket-viewport="true"
      role="region"
      aria-label={label}
      tabIndex={0}
      ref={viewportRef}
      className={className}
      onPointerDown={startPan}
      onPointerMove={movePan}
      onPointerUp={endPan}
      onPointerCancel={endPan}
      onLostPointerCapture={endPan}
      onKeyDown={handleKeyDown}
      style={{
        touchAction: 'none',
        flex: isFullscreen ? '1 1 0%' : undefined,
        minHeight: isFullscreen ? 0 : undefined,
        cursor: 'grab',
      }}
    >
      <div
        data-bracket-stage="true"
        ref={stageRef}
        className="absolute left-0 top-0 isolate min-h-full min-w-full origin-top-left print:relative"
        style={{
          width: bracketWidth + BRACKET_STAGE_PADDING * 2,
          height: bracketHeight + BRACKET_STAGE_PADDING * 2,
          transform: isPrinting
            ? 'none'
            : `translate3d(${panRef.current.x}px, ${panRef.current.y}px, 0) scale(${zoom})`,
        }}
      >
        {backgroundLayerStyle ? (
          <div
            aria-hidden="true"
            data-bracket-background="true"
            className={cn(
              'pointer-events-none absolute inset-0 z-0 overflow-hidden',
              !printBackground && 'print:hidden',
            )}
          >
            <div className="absolute -inset-4" style={backgroundLayerStyle} />
            <div className="absolute inset-0" style={backgroundOverlayStyle} />
          </div>
        ) : null}
        <div
          data-bracket-content="true"
          className="relative z-10"
          style={getBracketContentPlacementStyle({
            bracketHeight,
            bracketWidth,
            position,
          })}
        >
          {children(renderAllMatches ? null : area)}
        </div>
      </div>
    </div>
  )
}
